<script setup lang="ts">
  import { computed, ref } from 'vue';

  import type { UserListItem } from '@stuut/api-sdk/models';
  import { UserRoleType } from '@stuut/api-sdk/models';
  import { Badge } from '@ui/components/badge';
  import { Button } from '@ui/components/button';
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuTrigger,
  } from '@ui/components/dropdown-menu';
  import { toast } from '@ui/components/sonner';

  import { stuut } from '@/api';
  import DeactivateUserConfirmationDialog from '@/components/users/DeactivateUserConfirmationDialog.vue';
  import { sdk } from '@/sdk';
  import { useProfile } from '@/store';
  import { getApiErrorMessage } from '@/utils/apiErrors';

  interface Props {
    user: UserListItem;
  }

  const props = defineProps<Props>();
  const emit = defineEmits<{
    userUpdated: [user: Pick<UserListItem, 'id'> & Partial<UserListItem>];
  }>();

  const profileStore = useProfile();

  const canChangeRole = computed(() => {
    return (
      profileStore.profile?.id !== props.user.id && Boolean(profileStore.profile?.is_org_admin)
    );
  });

  const userRoles = [
    {
      name: 'Member',
      description: 'Can view data.',
      value: UserRoleType.Member,
    },
    {
      name: 'Admin',
      description: 'Can view data, grant user access, update user roles, and manage billing.',
      value: UserRoleType.Admin,
    },
    {
      name: 'Manager',
      description: 'Can view all activity for their assigned business unit.',
      value: UserRoleType.Manager,
    },
  ];

  const availableRoles = computed(() =>
    userRoles.filter(
      (role) => role.value !== UserRoleType.Manager || profileStore.hasBusinessUnits,
    ),
  );

  const roleLabel = computed(() => {
    return userRoles.find((role) => role.value === props.user.role)?.name ?? props.user.role;
  });

  const stateLabel = computed(() => (props.user.isAccDisabled ? 'Deactivated' : roleLabel.value));

  const isDeactivateDialogOpen = ref(false);

  const isUpdating = ref(false);

  const grantAccess = () => {
    if (isUpdating.value) return;
    isUpdating.value = true;

    stuut.users
      .grantAccess(props.user.id)
      .then(() => {
        emit('userUpdated', { id: props.user.id, isAccDisabled: false });
        toast.success(`${props.user.name} has been granted access.`);
      })
      .catch((err: unknown) => {
        toast.error(getApiErrorMessage(err, 'Could not grant access'));
      })
      .finally(() => (isUpdating.value = false));
  };

  const updateRole = async (role: UserRoleType) => {
    if (isUpdating.value) return;
    isUpdating.value = true;
    try {
      const updatedUser = await sdk.users.updateUserRole({
        userId: props.user.id,
        body: { role },
      });

      emit('userUpdated', { ...updatedUser.user, id: props.user.id });
      toast.success(`${props.user.name}'s role has been updated.`);
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Could not update role'));
    } finally {
      isUpdating.value = false;
    }
  };

  const onUserDeactivated = () => {
    emit('userUpdated', { id: props.user.id, isAccDisabled: true });
  };
</script>

<template>
  <DropdownMenu v-if="canChangeRole">
    <DropdownMenuTrigger as-child>
      <Button
        variant="outline"
        class="ml-auto disabled:opacity-100 border min-w-24 justify-between"
        role="combobox"
        size="sm"
        :disabled="!canChangeRole || isUpdating"
      >
        {{ stateLabel }}
        <div class="w-4">
          <i v-if="isUpdating" class="fa far fa-spinner-third animate-spin" />
          <i v-else class="fa far fa-chevron-down" />
        </div>
      </Button>
    </DropdownMenuTrigger>

    <DropdownMenuContent class="w-72">
      <DropdownMenuGroup v-if="!user.isAccDisabled">
        <DropdownMenuItem
          v-for="role in availableRoles"
          :key="role.value"
          :value="role.value"
          class="flex flex-col items-start px-4 py-2 space-y-0.5"
          @click="updateRole(role.value)"
        >
          <p class="font-semibold">{{ role.name }}</p>
          <p class="text-muted-foreground">
            {{ role.description }}
          </p>
        </DropdownMenuItem>
        <DropdownMenuItem
          class="flex flex-col items-start px-4 py-2 space-y-0.5"
          @click="isDeactivateDialogOpen = true"
        >
          <div class="flex items-center gap-2 font-semibold">
            <i class="far fa-lock text-2xs text-destructive" />
            <p>Deactivate user</p>
          </div>
          <p class="text-muted-foreground">Revokes user's access to your organization.</p>
        </DropdownMenuItem>
      </DropdownMenuGroup>

      <DropdownMenuGroup v-else>
        <DropdownMenuItem icon @click="grantAccess">
          <p>Re-grant Access</p>
          <i class="fa far fa-check" />
        </DropdownMenuItem>
      </DropdownMenuGroup>
    </DropdownMenuContent>
  </DropdownMenu>

  <Badge
    v-else
    class="font-medium h-8 ml-auto"
    :class="user.isAccDisabled && 'text-muted-foreground'"
    :variant="user.isAccDisabled ? 'secondary' : 'outline'"
  >
    {{ stateLabel }}
  </Badge>

  <DeactivateUserConfirmationDialog
    v-if="isDeactivateDialogOpen"
    v-model:open="isDeactivateDialogOpen"
    :user
    @deactivated="onUserDeactivated"
  />
</template>

<style scoped></style>
